import { editorUrl } from '@/lib/editorBasePath';
import React, { useRef, useState, useEffect } from 'react';
import { YearbookElement, TextStyle } from '@/types/yearbook';
import { Plus, Trash2, ChevronDown, Check, AlignLeft, AlignCenter, AlignRight, AlignJustify, ChevronsUpDown, PaintBucket } from 'lucide-react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { FONT_NAMES, ensureFontLoaded, fontAllowsBold, fontAllowsItalic } from '@/lib/fonts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';

interface TextPanelProps {
  onAddText: (variant?: 'title' | 'subtitle' | 'text') => void;
  selectedElement: YearbookElement | null;
  onUpdateElement: (id: string, updates: Partial<YearbookElement>) => void;
  favColors: string[];
  onFavColorsChange: (colors: string[]) => void;
}

const fontOptions = FONT_NAMES;

/* Charge la police d'une ligne du menu dès qu'elle devient visible */
const FontOption: React.FC<{ font: string; selected: boolean; onSelect: () => void }> = ({ font, selected, onSelect }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      ensureFontLoaded(font);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        ensureFontLoaded(font);
        obs.disconnect();
      }
    }, { threshold: 0.01 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [font]);

  return (
    <button
      ref={btnRef}
      onClick={onSelect}
      className={`w-full text-left px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${
        selected ? 'bg-accent/50 font-medium' : ''
      }`}
      style={{ fontFamily: font }}
    >
      {font}
    </button>
  );
};

/* ---------- Font selector with preview ---------- */
const FontSelector: React.FC<{ value: string; onChange: (f: string) => void; options: string[] }> = ({
  value,
  onChange,
  options,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { ensureFontLoaded(value); }, [value]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-editor-sidebar-hover text-editor-sidebar-foreground text-sm rounded-md px-2 py-1.5 border border-editor-sidebar-border hover:border-editor-sidebar-active/40 transition-colors"
      >
        <span style={{ fontFamily: value }}>{value}</span>
        <ChevronsUpDown size={12} className="opacity-50 shrink-0 ml-1" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
          {options.map((f) => (
            <FontOption
              key={f}
              font={f}
              selected={f === value}
              onSelect={() => { onChange(f); setOpen(false); }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TextPanel: React.FC<TextPanelProps> = ({ onAddText, selectedElement, onUpdateElement, favColors, onFavColorsChange }) => {
  const colorPickerRef = useRef<HTMLInputElement>(null);
  const [pendingColor, setPendingColor] = useState('#000000');
  const [pickerOpen, setPickerOpen] = useState(false);
  const updateTextStyle = (updates: Partial<TextStyle>) => {
    if (!selectedElement || selectedElement.type !== 'text') return;
    onUpdateElement(selectedElement.id, {
      textStyle: { ...selectedElement.textStyle!, ...updates },
    });
  };

  const isTextSelected = selectedElement?.type === 'text' && selectedElement.textStyle;

  const handleAddColor = () => {
    const color = pendingColor;
    onFavColorsChange([...favColors, color]);
    setPickerOpen(false);
    setPendingColor('#000000');
    fetch(editorUrl('/save_favorites_colors'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_color: color }),
    }).catch(() => {
      onFavColorsChange(favColors.filter((c) => c !== color));
    });
  };

  const handleRemoveColor = (color: string) => {
    onFavColorsChange(favColors.filter((c) => c !== color));
    fetch(editorUrl('/remove_favorites_colors'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ color }),
    }).catch(() => {
      onFavColorsChange([...favColors, color]);
    });
  };

  const applyColor = (color: string) => {
    if (isTextSelected) {
      updateTextStyle({ color });
    }
  };

  const isBold = isTextSelected && Number(selectedElement.textStyle!.fontWeight) >= 700;

  return (
    <div className="space-y-3">
      {/* Add text dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="w-full py-2.5 px-3 rounded-md text-sm bg-primary-gradient text-primary-foreground transition-colors flex items-center justify-center gap-1.5">
            <Plus size={14} />
            Ajouter un texte
            <ChevronDown size={12} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem onClick={() => onAddText('title')}>
            <div>
              <p className="font-bold text-sm">Titre</p>
              <p className="text-[10px] text-muted-foreground">Gros texte en haut de la page</p>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddText('subtitle')}>
            <div>
              <p className="text-sm italic">Sous-titre</p>
              <p className="text-[10px] text-muted-foreground">Texte italique sous le titre</p>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddText('text')}>
            <div>
              <p className="text-sm">Texte</p>
              <p className="text-[10px] text-muted-foreground">Texte au milieu de la page</p>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Favorite colors */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-muted-foreground">Mes couleurs préférées</h4>
        <div className="grid grid-cols-6 gap-1.5">
          {favColors.map((color, idx) => (
            <div key={`${color}-${idx}`} className="relative group aspect-square">
              <button
                onClick={() => applyColor(color)}
                className={`w-full h-full rounded-md border transition-transform hover:scale-110 ${
                  isTextSelected && selectedElement.textStyle?.color === color
                    ? 'border-editor-sidebar-active ring-1 ring-editor-sidebar-active'
                    : 'border-editor-sidebar-border'
                }`}
                style={{ backgroundColor: color }}
              />
              <button
                onClick={() => handleRemoveColor(color)}
                className="absolute top-0 right-0 p-0.5 rounded-bl-md rounded-tr-md bg-black/60 hover:bg-black/80 transition-colors opacity-0 group-hover:opacity-100"
                title="Supprimer cette couleur"
              >
                <Trash2 size={10} className="text-white" />
              </button>
            </div>
          ))}
          <div className="aspect-square">
            <button
              onClick={() => setPickerOpen(true)}
              className="w-full h-full rounded-md border border-dashed border-editor-sidebar-border hover:border-editor-sidebar-active transition-colors flex items-center justify-center"
              title="Ajouter une couleur"
            >
              <Plus size={12} className="text-muted-foreground" />
            </button>
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
              <DialogContent className="sm:max-w-sm p-0 gap-0 overflow-hidden">
                <DialogTitle className="px-5 pt-5 pb-0 text-sm font-semibold">Choisir une couleur</DialogTitle>
                <div className="p-5 space-y-4">
                  <HexColorPicker color={pendingColor} onChange={setPendingColor} style={{ width: '100%', height: 180 }} />
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg border border-border shrink-0 shadow-sm" style={{ backgroundColor: pendingColor }} />
                    <HexColorInput
                      color={pendingColor}
                      onChange={setPendingColor}
                      prefixed
                      className="flex-1 text-sm font-mono bg-muted text-foreground rounded-md px-3 py-2 border border-border uppercase"
                    />
                    <button
                      onClick={handleAddColor}
                      className="flex items-center gap-1.5 text-sm bg-primary-gradient text-primary-foreground px-4 py-2 rounded-md hover:opacity-90 transition-opacity shrink-0 font-medium"
                    >
                      <Check size={14} />
                      Ajouter
                    </button>
                  </div>
                </div>
                {favColors.length > 0 && (
                  <div className="border-t border-border px-5 py-4 bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-2">Mes couleurs enregistrées</p>
                    <div className="flex flex-wrap gap-2">
                      {favColors.map((color, idx) => (
                        <div key={`modal-${color}-${idx}`} className="relative group">
                          <button
                            onClick={() => setPendingColor(color)}
                            className="w-8 h-8 rounded-lg border border-border hover:scale-110 transition-transform shadow-sm"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                          <button
                            onClick={() => handleRemoveColor(color)}
                            className="absolute -top-1 -right-1 p-0.5 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {isTextSelected && (
        <div className="space-y-3 border-t border-editor-sidebar-border pt-3">
          <div className="relative">
            <label className="text-xs text-muted-foreground block mb-1">Police</label>
            <FontSelector
              value={selectedElement.textStyle!.fontFamily}
              onChange={(f) => updateTextStyle({ fontFamily: f })}
              options={fontOptions}
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Taille</label>
            <input
              type="number"
              value={selectedElement.textStyle!.fontSize}
              onChange={(e) => updateTextStyle({ fontSize: Number(e.target.value) })}
              min={8}
              max={120}
              className="w-full bg-editor-sidebar-hover text-editor-sidebar-foreground text-sm rounded-md px-2 py-1.5 border border-editor-sidebar-border"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Couleur du texte</label>
            <input
              type="color"
              value={selectedElement.textStyle!.color}
              onChange={(e) => updateTextStyle({ color: e.target.value })}
              className="w-full h-8 rounded-md cursor-pointer border border-editor-sidebar-border"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1">
              <span className="flex items-center gap-1">
                <PaintBucket size={12} />
                Fond du bloc
              </span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={selectedElement.backgroundColor || '#ffffff'}
                onChange={(e) => onUpdateElement(selectedElement.id, { backgroundColor: e.target.value })}
                className="flex-1 h-8 rounded-md cursor-pointer border border-editor-sidebar-border"
              />
              {selectedElement.backgroundColor && (
                <button
                  onClick={() => onUpdateElement(selectedElement.id, { backgroundColor: undefined })}
                  className="text-[10px] px-2 py-1.5 rounded-md bg-editor-sidebar-hover text-muted-foreground hover:text-foreground transition-colors border border-editor-sidebar-border"
                >
                  Retirer
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-1">
            {([
              { align: 'left' as const, Icon: AlignLeft },
              { align: 'center' as const, Icon: AlignCenter },
              { align: 'right' as const, Icon: AlignRight },
              { align: 'justify' as const, Icon: AlignJustify },
            ]).map(({ align, Icon }) => (
              <button
                key={align}
                onClick={() => updateTextStyle({ textAlign: align })}
                className={`flex-1 py-1.5 rounded-md transition-colors flex items-center justify-center ${
                  selectedElement.textStyle?.textAlign === align
                    ? 'bg-primary-gradient text-primary-foreground'
                    : 'bg-editor-sidebar-hover text-editor-sidebar-foreground'
                }`}
              >
                <Icon size={14} />
              </button>
            ))}
          </div>

          {(() => {
            const font = selectedElement.textStyle?.fontFamily || 'DM Sans';
            const noBold = !fontAllowsBold(font);
            const noItalic = !fontAllowsItalic(font);
            return (
              <div className="flex gap-1">
                <button
                  disabled={noBold}
                  onClick={() => !noBold && updateTextStyle({ fontWeight: isBold ? '400' : '700' })}
                  className={`flex-1 py-1.5 text-sm rounded-md font-bold transition-colors ${
                    noBold
                      ? 'opacity-30 cursor-not-allowed bg-editor-sidebar-hover text-editor-sidebar-foreground'
                      : isBold
                        ? 'bg-primary-gradient text-primary-foreground'
                        : 'bg-editor-sidebar-hover text-editor-sidebar-foreground'
                  }`}
                >
                  B
                </button>
                <button
                  disabled={noItalic}
                  onClick={() => !noItalic && updateTextStyle({ italic: !selectedElement.textStyle?.italic })}
                  className={`flex-1 py-1.5 text-sm rounded-md italic transition-colors ${
                    noItalic
                      ? 'opacity-30 cursor-not-allowed bg-editor-sidebar-hover text-editor-sidebar-foreground'
                      : selectedElement.textStyle?.italic
                        ? 'bg-primary-gradient text-primary-foreground'
                        : 'bg-editor-sidebar-hover text-editor-sidebar-foreground'
                  }`}
                >
                  I
                </button>
                <button
                  onClick={() => updateTextStyle({ underline: !selectedElement.textStyle?.underline })}
                  className={`flex-1 py-1.5 text-sm rounded-md underline transition-colors ${
                    selectedElement.textStyle?.underline
                      ? 'bg-primary-gradient text-primary-foreground'
                      : 'bg-editor-sidebar-hover text-editor-sidebar-foreground'
                  }`}
                >
                  U
                </button>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default TextPanel;
